import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";

const hopping = new Set();

export function isSsoRequiredError(error) {
  if (error?.response?.status !== 403) return false;
  const data = error.response.data;
  const code = data?.code || data?.message?.code;
  return code === "sso_required";
}

export function ssoRequiredOrgId(error) {
  const data = error?.response?.data;
  return data?.org_id || data?.message?.org_id || null;
}

export function isOrgSsoUrl(url = "") {
  return (
    /\/org\/[^/]+\/sso\/(status|config|start|recover|recovery-codes|directory|bindings|reset)(\/|$|\?)/.test(
      url
    ) || url.includes("/org-sso/callback")
  );
}

export function isOrgSsoEnterPath(pathname = "") {
  return /^\/orgs\/[^/]+\/sso\/?$/.test(pathname);
}

export function orgSsoEnterUrl(orgId, returnPath) {
  const base = `/orgs/${encodeURIComponent(orgId)}/sso`;
  const params = new URLSearchParams();
  let ret = returnPath ? String(returnPath) : "";
  let error = "";
  if (ret) {
    const qIndex = ret.indexOf("?");
    if (qIndex >= 0) {
      const pathOnly = ret.slice(0, qIndex);
      const q = new URLSearchParams(ret.slice(qIndex + 1));
      error = q.get("sso_error") || "";
      q.delete("sso_error");
      const rest = q.toString();
      ret = rest ? `${pathOnly}?${rest}` : pathOnly;
    }
  }
  if (error) params.set("sso_error", error);
  if (ret) params.set("return", ret);
  const q = params.toString();
  return q ? `${base}?${q}` : base;
}

export function navigateToOrgSsoEnter(orgId, returnPath) {
  if (!orgId || typeof window === "undefined") return;
  if (isOrgSsoEnterPath(window.location.pathname)) return;
  const ret =
    returnPath || `${window.location.pathname}${window.location.search}`;
  window.location.assign(orgSsoEnterUrl(orgId, ret));
}

/**
 * Authenticated start → full-page navigate to the IdP.
 * @returns {Promise<boolean>} true if navigation began
 */
export async function startOrgSsoHop(
  orgId,
  { mode = "workplace", returnPath, prompt } = {}
) {
  if (!orgId || hopping.has(orgId)) return false;
  hopping.add(orgId);
  try {
    const raw =
      returnPath ||
      (typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : `/orgs/${orgId}`);
    const path = ssoReturnPath(orgId, raw);
    const body = { mode, return_path: path };
    if (prompt) body.prompt = prompt;
    const res = await axios.post(
      `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/start`,
      body
    );
    const url = res.data?.authorization_url;
    if (!url) {
      hopping.delete(orgId);
      throw new Error("SSO start did not return an authorization URL");
    }
    window.location.assign(url);
    return true;
  } catch (err) {
    hopping.delete(orgId);
    throw err;
  }
}

export async function recoverOrgSso(orgId, { password, code }) {
  const res = await axios.post(
    `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/recover`,
    { password, code }
  );
  return res.data;
}

export function ssoErrorMessage(code) {
  const map = {
    sso_invite_sub_mismatch:
      "Sign in at the identity provider as the directory person you were invited as.",
    sso_bound_to_other:
      "This identity provider account is already linked to a different Circa user in this workplace (or your Circa account is linked to a different IdP user). Ask an admin to unlink IdP in Members or Security, then try again.",
    sso_issuer_change_requires_clear:
      "Changing the identity provider requires clearing existing SSO links. Confirm that step and save again.",
    sso_failed: "SSO could not be completed. Try again, or contact an admin.",
    invalid_state: "This SSO attempt expired. Start again from Circa.",
    missing_code: "The identity provider did not return a code.",
    not_configured: "SSO is not fully configured for this workplace.",
    access_denied: "Sign-in was cancelled at the identity provider.",
    sso_recovery_exhausted:
      "No recovery codes remain. Contact Circa support to disable workplace SSO.",
  };
  return map[code] || "SSO could not be completed.";
}

/**
 * Safe post-SSO return destinations for a workplace.
 * Keep in sync with API allowlistedReturnPath.
 */
export function ssoReturnPath(orgId, raw) {
  const fallback = `/orgs/${orgId}`;
  const path = String(raw || "").trim() || fallback;
  if (!path.startsWith("/") || path.startsWith("//")) return fallback;
  if (path.includes("://") || path.includes("..")) return fallback;

  const pathOnly = path.split("?")[0].replace(/\/$/, "") || "/";
  if (isOrgSsoEnterPath(pathOnly) || pathOnly === `/orgs/${orgId}/sso`) {
    return fallback;
  }

  if (pathOnly === "/dashboard") return path;

  const oPrefix = `/o/${orgId}`;
  if (pathOnly === `${oPrefix}/my-work`) return path;
  if (pathOnly === `${oPrefix}/messages` || pathOnly.startsWith(`${oPrefix}/messages/`)) {
    return path;
  }

  const prefix = `/orgs/${orgId}`;
  if (
    pathOnly === prefix ||
    path.startsWith(`${prefix}/`) ||
    path.startsWith(`${prefix}?`)
  ) {
    return path;
  }

  return fallback;
}
